/**
 * CloudFront URL Helper
 *
 * Standalone utility for converting S3 URLs to CloudFront URLs.
 * This can be used in any component without needing access to sequencer or AudioBufferCache.
 *
 * Architecture Note:
 * - S3 URLs are the source of truth (stored in nodes/database)
 * - CloudFront URLs should only be used at load time (thumbnails, video playback, etc.)
 * - This helper provides the conversion at the consumption point
 *
 * Environment Detection:
 * - Works in both Production Trebble Editor and Remotion Studio
 * - Converts S3 URLs to CloudFront for better CORS support
 *
 * IMPORTANT: This file must NOT import Utils.js or any Trebble infrastructure
 * to remain compatible with Remotion Studio standalone mode.
 */

/**
 * S3 bucket URL prefixes mapped to their CloudFront distribution URLs.
 * This is the SINGLE SOURCE OF TRUTH for all S3-to-CloudFront URL mappings.
 * Other files (Utils.js, MediaUrlResolverService.ts) import from here.
 */
export const S3_PREFIX_TO_CLOUDFRONT_PREFIX: Record<string, string> = {
    // Original audio editor mappings
    "https://s3.amazonaws.com/com.trebble.trebble.speechsynthesis": "https://d9cefelymtnnq.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/com.trebble.trebble.speechsynthesis": "https://d9cefelymtnnq.cloudfront.net",
    "https://com.trebble.trebble.speechsynthesis.s3.amazonaws.com": "https://d9cefelymtnnq.cloudfront.net",
    "https://com.trebble.trebble.speechsynthesis.s3.us-east-1.amazonaws.com": "https://d9cefelymtnnq.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.tts": "https://d4ewei14y66a3.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/com.trebble.tts": "https://d4ewei14y66a3.cloudfront.net",
    "https://com.trebble.tts.s3.amazonaws.com": "https://d4ewei14y66a3.cloudfront.net",
    "https://com.trebble.tts.s3.us-east-1.amazonaws.com": "https://d4ewei14y66a3.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.capsules.replies": "https://d1prtj5fpply6k.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/com.trebble.capsules.replies": "https://d1prtj5fpply6k.cloudfront.net",
    "https://com.trebble.capsules.replies.s3.amazonaws.com": "https://d1prtj5fpply6k.cloudfront.net",
    "https://com.trebble.capsules.replies.s3.us-east-1.amazonaws.com": "https://d1prtj5fpply6k.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.songs.hightlights": "https://d8rt3fd3sz1dv.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/com.trebble.songs.hightlights": "https://d8rt3fd3sz1dv.cloudfront.net",
    "https://com.trebble.songs.hightlights.s3.amazonaws.com": "https://d8rt3fd3sz1dv.cloudfront.net",
    "https://com.trebble.songs.hightlights.s3.us-east-1.amazonaws.com": "https://d8rt3fd3sz1dv.cloudfront.net",
    "https://s3.amazonaws.com/fm.trebble.bigsoundbank": "https://djq8hogjd3s83.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/fm.trebble.bigsoundbank": "https://djq8hogjd3s83.cloudfront.net",
    "https://fm.trebble.bigsoundbank.s3.amazonaws.com": "https://djq8hogjd3s83.cloudfront.net",
    "https://fm.trebble.bigsoundbank.s3.us-east-1.amazonaws.com": "https://djq8hogjd3s83.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.trebble.userbackgroundmusic": "https://d3rup12ktram15.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/com.trebble.trebble.userbackgroundmusic": "https://d3rup12ktram15.cloudfront.net",
    "https://com.trebble.trebble.userbackgroundmusic.s3.amazonaws.com": "https://d3rup12ktram15.cloudfront.net",
    "https://com.trebble.trebble.userbackgroundmusic.s3.us-east-1.amazonaws.com": "https://d3rup12ktram15.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.capsules.plain": "https://d8rxm027o5hsy.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/com.trebble.capsules.plain": "https://d8rxm027o5hsy.cloudfront.net",
    "https://com.trebble.capsules.plain.s3.amazonaws.com": "https://d8rxm027o5hsy.cloudfront.net",
    "https://com.trebble.capsules.plain.s3.us-east-1.amazonaws.com": "https://d8rxm027o5hsy.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.trebble.audio.recordings": "https://d2npgja6i70r1g.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/com.trebble.trebble.audio.recordings": "https://d2npgja6i70r1g.cloudfront.net",
    "https://com.trebble.trebble.audio.recordings.s3.amazonaws.com": "https://d2npgja6i70r1g.cloudfront.net",
    "https://com.trebble.trebble.audio.recordings.s3.us-east-1.amazonaws.com": "https://d2npgja6i70r1g.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.trebble.defaultbackgroundmusic": "https://d3qdis5gy6z0kb.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/com.trebble.trebble.defaultbackgroundmusic": "https://d3qdis5gy6z0kb.cloudfront.net",
    "https://com.trebble.trebble.defaultbackgroundmusic.s3.amazonaws.com": "https://d3qdis5gy6z0kb.cloudfront.net",
    "https://com.trebble.trebble.defaultbackgroundmusic.s3.us-east-1.amazonaws.com": "https://d3qdis5gy6z0kb.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.thirdparty.media.cache": "https://d2vzem201j0xmn.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/com.trebble.thirdparty.media.cache": "https://d2vzem201j0xmn.cloudfront.net",
    "https://com.trebble.thirdparty.media.cache.s3.amazonaws.com": "https://d2vzem201j0xmn.cloudfront.net",
    "https://com.trebble.thirdparty.media.cache.s3.us-east-1.amazonaws.com": "https://d2vzem201j0xmn.cloudfront.net",
    // User media and video mappings
    "https://s3.amazonaws.com/com.trebble.users.trebbles.originals": "https://d1tewqwobn2tgz.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.trebble.jingles": "https://dqxtgubqapfce.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.users.capsule.attachments": "https://d38fkees2wm0u8.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.trebble.effects": "https://d2vfazr1qsiqah.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.users.ai.generated.audio": "https://d3bk9gmz8rsr9c.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.users.trebbles.enhanced": "https://d1rfotpf07uy2t.cloudfront.net",
    "https://s3.amazonaws.com/trebble-videos": "https://d1ht0b9h4dw11.cloudfront.net",
    "https://s3.amazonaws.com/com.trebble.users.media.assets": "https://d1y29rh14y1py8.cloudfront.net",
    // Regional S3 URL variant (same bucket, different URL format)
    "https://s3.us-east-1.amazonaws.com/com.trebble.users.media.assets": "https://d1y29rh14y1py8.cloudfront.net",
    "https://com.trebble.users.media.assets.s3.amazonaws.com": "https://d1y29rh14y1py8.cloudfront.net",
    "https://com.trebble.users.media.assets.s3.us-east-1.amazonaws.com": "https://d1y29rh14y1py8.cloudfront.net",
    // Video/audio derived asset buckets
    "https://s3.amazonaws.com/fm.trebble.audio.waveform.peaks": "https://d3i6krpqazskis.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/fm.trebble.audio.waveform.peaks": "https://d3i6krpqazskis.cloudfront.net",
    "https://fm.trebble.audio.waveform.peaks.s3.amazonaws.com": "https://d3i6krpqazskis.cloudfront.net",
    "https://fm.trebble.audio.waveform.peaks.s3.us-east-1.amazonaws.com": "https://d3i6krpqazskis.cloudfront.net",
    "https://s3.amazonaws.com/fm.trebble.video.thumbnails": "https://dsj4le7osln2t.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/fm.trebble.video.thumbnails": "https://dsj4le7osln2t.cloudfront.net",
    "https://fm.trebble.video.thumbnails.s3.amazonaws.com": "https://dsj4le7osln2t.cloudfront.net",
    "https://fm.trebble.video.thumbnails.s3.us-east-1.amazonaws.com": "https://dsj4le7osln2t.cloudfront.net",
    "https://s3.amazonaws.com/fm.trebble.video.rendered": "https://dvvafaj1sdbsc.cloudfront.net",
    "https://s3.us-east-1.amazonaws.com/fm.trebble.video.rendered": "https://dvvafaj1sdbsc.cloudfront.net",
    "https://fm.trebble.video.rendered.s3.amazonaws.com": "https://dvvafaj1sdbsc.cloudfront.net",
    "https://fm.trebble.video.rendered.s3.us-east-1.amazonaws.com": "https://dvvafaj1sdbsc.cloudfront.net",
};

const S3_BUCKET_TO_CLOUDFRONT_PREFIX: Record<string, string> = {
    "com.trebble.trebble.speechsynthesis": "https://d9cefelymtnnq.cloudfront.net",
    "com.trebble.tts": "https://d4ewei14y66a3.cloudfront.net",
    "com.trebble.capsules.replies": "https://d1prtj5fpply6k.cloudfront.net",
    "com.trebble.songs.hightlights": "https://d8rt3fd3sz1dv.cloudfront.net",
    "fm.trebble.bigsoundbank": "https://djq8hogjd3s83.cloudfront.net",
    "com.trebble.trebble.userbackgroundmusic": "https://d3rup12ktram15.cloudfront.net",
    "com.trebble.capsules.plain": "https://d8rxm027o5hsy.cloudfront.net",
    "com.trebble.trebble.audio.recordings": "https://d2npgja6i70r1g.cloudfront.net",
    "com.trebble.trebble.defaultbackgroundmusic": "https://d3qdis5gy6z0kb.cloudfront.net",
    "com.trebble.thirdparty.media.cache": "https://d2vzem201j0xmn.cloudfront.net",
    "com.trebble.users.trebbles.originals": "https://d1tewqwobn2tgz.cloudfront.net",
    "com.trebble.trebble.jingles": "https://dqxtgubqapfce.cloudfront.net",
    "com.trebble.users.capsule.attachments": "https://d38fkees2wm0u8.cloudfront.net",
    "com.trebble.trebble.effects": "https://d2vfazr1qsiqah.cloudfront.net",
    "com.trebble.users.ai.generated.audio": "https://d3bk9gmz8rsr9c.cloudfront.net",
    "com.trebble.users.trebbles.enhanced": "https://d1rfotpf07uy2t.cloudfront.net",
    "trebble-videos": "https://d1ht0b9h4dw11.cloudfront.net",
    "com.trebble.users.media.assets": "https://d1y29rh14y1py8.cloudfront.net",
    "fm.trebble.audio.waveform.peaks": "https://d3i6krpqazskis.cloudfront.net",
    "fm.trebble.video.thumbnails": "https://dsj4le7osln2t.cloudfront.net",
    "fm.trebble.video.rendered": "https://dvvafaj1sdbsc.cloudfront.net",
};

function replaceMatchingS3Prefix(url: string, s3Prefix: string, cloudFrontPrefix: string): string | null {
    if (url === s3Prefix || url.startsWith(`${s3Prefix}/`) || url.startsWith(`${s3Prefix}?`)) {
        return url.replace(s3Prefix, cloudFrontPrefix);
    }

    return null;
}

function getCloudFrontUrlFromS3Url(url: string): string | null {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }

    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        return null;
    }

    const host = parsed.hostname;
    let bucketName = '';
    let objectPath = '';

    if (host === 's3.amazonaws.com' || /^s3[.-][a-z0-9-]+\.amazonaws\.com$/i.test(host)) {
        const pathWithoutLeadingSlash = parsed.pathname.startsWith('/')
            ? parsed.pathname.slice(1)
            : parsed.pathname;
        const bucketSeparatorIndex = pathWithoutLeadingSlash.indexOf('/');

        if (bucketSeparatorIndex === -1) {
            bucketName = pathWithoutLeadingSlash;
            objectPath = '';
        } else {
            bucketName = pathWithoutLeadingSlash.slice(0, bucketSeparatorIndex);
            objectPath = pathWithoutLeadingSlash.slice(bucketSeparatorIndex);
        }
    } else {
        const virtualHostedMatch = host.match(/^(.+)\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i);
        if (!virtualHostedMatch) {
            return null;
        }
        bucketName = virtualHostedMatch[1];
        objectPath = parsed.pathname || '';
    }

    const cloudFrontPrefix = S3_BUCKET_TO_CLOUDFRONT_PREFIX[bucketName];
    if (!cloudFrontPrefix) {
        return null;
    }

    return `${cloudFrontPrefix}${objectPath}${parsed.search}${parsed.hash}`;
}

/**
 * Convert an S3 URL to its CloudFront equivalent for CORS-safe loading.
 *
 * @deprecated For media playback (video, audio, images), use `useResolvedMediaUrl()`
 * or `useResolvedMediaUrlSync()` from `components/helper/useResolvedMediaUrl.ts` instead.
 * Those hooks go through `MediaUrlResolverService` which checks OPFS cache and session
 * blob cache BEFORE falling back to CloudFront conversion — giving zero-network playback
 * when the file is cached. Direct CloudFront conversion bypasses these caches entirely.
 *
 * This function should ONLY be used for:
 * - Static asset URLs (sound effects, library assets) resolved at module load time
 * - CSS mask-image URLs (not media playback)
 * - Non-React contexts where hooks cannot be used AND MediaUrlResolverService.resolveUrlSync()
 *   is not a suitable alternative
 *
 * @param s3Url - The S3 URL to convert
 * @returns The CloudFront URL if matching prefix found, original URL otherwise
 *
 * @example
 * const cfUrl = getCloudFrontUrl("https://s3.amazonaws.com/com.trebble.users.media.assets/video.mp4");
 * // Returns: "https://d1y29rh14y1py8.cloudfront.net/video.mp4"
 */
export function getCloudFrontUrl(s3Url: string | null | undefined): string {
    if (!s3Url) {
        return s3Url || '';
    }

    // Check each S3 prefix and convert to CloudFront if matched
    for (const s3Prefix in S3_PREFIX_TO_CLOUDFRONT_PREFIX) {
        const cloudFrontUrl = replaceMatchingS3Prefix(s3Url, s3Prefix, S3_PREFIX_TO_CLOUDFRONT_PREFIX[s3Prefix]);
        if (cloudFrontUrl) {
            return cloudFrontUrl;
        }
    }

    const cloudFrontUrl = getCloudFrontUrlFromS3Url(s3Url);
    if (cloudFrontUrl) {
        return cloudFrontUrl;
    }

    return s3Url;
}

/**
 * Check if a URL is an S3 URL that can be converted to CloudFront
 *
 * @param url - The URL to check
 * @returns true if the URL is a convertible S3 URL
 */
export function isConvertibleS3Url(url: string | null | undefined): boolean {
    if (!url) {
        return false;
    }

    // Check if URL matches any known S3 prefix
    for (const s3Prefix in S3_PREFIX_TO_CLOUDFRONT_PREFIX) {
        if (replaceMatchingS3Prefix(url, s3Prefix, S3_PREFIX_TO_CLOUDFRONT_PREFIX[s3Prefix])) {
            return true;
        }
    }

    if (getCloudFrontUrlFromS3Url(url)) {
        return true;
    }

    return false;
}

export default {
    getCloudFrontUrl,
    isConvertibleS3Url,
};
